import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError , toastError} from "../../lib/api";
import { PageHeader, EmptyState } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Shield, ShieldCheck, ShieldX } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

export default function SupportAccess() {
  const { setSupportSession, supportSession } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [reason, setReason] = useState("");
  const [duration, setDuration] = useState(60);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [t, s] = await Promise.all([api.get("/platform/tenants"), api.get("/support-access/active")]);
      setTenants(t.data);
      setSessions(s.data);
    } catch (e) { toastError(e); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/support-access", {
        tenant_id: tenantId, reason, duration_minutes: Number(duration)
      }, { headers: { "Idempotency-Key": crypto.randomUUID() }});
      toast.success("Support session created");
      setOpen(false); setReason("");
      const tenant = tenants.find((item) => item.id === data.tenant_id);
      setSupportSession({ ...data, tenant_name: tenant?.name });
      navigate("/dashboard");
    } catch (e) { toastError(e); }
    finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/support-access/${id}/revoke`);
      toast.success("Revoked");
      if (supportSession?.id === id) setSupportSession(null);
      load();
    } catch (e) { toastError(e); }
  };

  const activateSession = (s) => {
    const tenant = tenants.find((item) => item.id === s.tenant_id);
    setSupportSession({ ...s, tenant_name: tenant?.name });
    toast.success("Opening tenant workspace");
    navigate("/dashboard");
  };

  return (
    <>
      <PageHeader title="Tenant Support Access" subtitle="Temporarily access a tenant workspace for authorized support. Every session and action is audited."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-support-btn"><Shield className="w-4 h-4 mr-1" /> New session</Button>
            </DialogTrigger>
            <DialogContent data-testid="create-support-dialog">
              <DialogHeader><DialogTitle>Start support session</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1"><Label>Tenant</Label>
                  <Select value={tenantId} onValueChange={setTenantId}>
                    <SelectTrigger data-testid="support-tenant"><SelectValue placeholder="Choose tenant" /></SelectTrigger>
                    <SelectContent>
                      {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.slug})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label>Reason (audited)</Label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="support-reason" />
                </div>
                <div className="space-y-1"><Label>Duration (minutes)</Label>
                  <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} min={5} max={480} data-testid="support-duration" />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={busy || !tenantId || reason.length < 5} data-testid="support-submit">
                  Create session
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="max-w-7xl mx-auto p-6 md:p-8">
        {sessions.length === 0 ? (
          <EmptyState icon={Shield} title="No active sessions"
            description="Create a support session to access a tenant's CRM data with an audit trail." />
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="support-sessions-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-widest">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Tenant</th>
                  <th className="text-left px-4 py-2.5 font-medium">Reason</th>
                  <th className="text-left px-4 py-2.5 font-medium">Expires</th>
                  <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id} className="border-t border-slate-200">
                    <td className="px-4 py-2.5 font-mono text-xs">{s.tenant_id.slice(0, 8)}…</td>
                    <td className="px-4 py-2.5 text-slate-700">{s.reason}</td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{new Date(s.expires_at).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right space-x-3">
                      <button className="text-xs text-emerald-600 hover:underline inline-flex items-center gap-1" onClick={() => activateSession(s)} data-testid={`activate-${s.id.slice(0,8)}`}>
                        <ShieldCheck className="w-3.5 h-3.5" /> Use in this browser
                      </button>
                      <button className="text-xs text-rose-600 hover:underline inline-flex items-center gap-1" onClick={() => revoke(s.id)} data-testid={`revoke-${s.id.slice(0,8)}`}>
                        <ShieldX className="w-3.5 h-3.5" /> Revoke
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
